import { Props, SelectInstance, type ChakraStylesConfig, type GroupBase } from 'chakra-react-select';
import { RefAttributes } from 'react';
import { theme } from '../shared/theme';
import { ResistomapSelect, resistomapSelectClassNamePrefix } from './resistomap-select';

// A drop-in replacement for ResistomapSelect that applies the specialised
// "filter-bar" styling used inside the main FilterBar component.
//
// Usage: simply import this component instead of ResistomapSelect when
// you are rendering a select inside the FilterBar. No extra props are
// required – all custom styling is applied automatically.

export function FilterBarResistomapSelect<
  T extends Option,
  IsMulti extends boolean,
  Group extends GroupBase<T> = GroupBase<T>,
>(props: Props<T, IsMulti, Group> & RefAttributes<SelectInstance<T, IsMulti, Group>>) {
  // Separate caller-supplied chakraStyles so we can merge our overrides.
  const {
    chakraStyles: chakraStylesProp,
    onMenuClose: callerOnMenuClose,
    ...rest
  } = props as unknown as {
    chakraStyles?: ChakraStylesConfig<T, IsMulti, Group>;
    onMenuClose?: () => void;
  } & Props<T, IsMulti, Group> &
    RefAttributes<SelectInstance<T, IsMulti, Group>>;

  // ---------------------------------------------------------------------
  // Helper utilities for composing style overrides
  // ---------------------------------------------------------------------
  type StyleObj = Record<string, unknown>;

  // Keep the select full-width inside its container.
  const containerStyle = (provided: StyleObj): StyleObj => ({
    ...provided,
    width: '100%',
  });

  // Adds the blue outline used for active filter controls.
  const addActiveBorder = (base: StyleObj, isActive: boolean): StyleObj => ({
    ...base,
    ...(isActive
      ? {
          borderColor: theme.colors.blue500,
          boxShadow: `0 0 0 1px ${theme.colors.blue500}`,
        }
      : {}),
  });

  const controlStyle = (provided: StyleObj, state: SelectState): StyleObj => {
    /* eslint-disable @typescript-eslint/no-unsafe-argument */
    const caller = chakraStylesProp?.control ? chakraStylesProp.control(provided, state as any) : provided;
    /* eslint-enable @typescript-eslint/no-unsafe-argument */
    const isActive = Boolean(state.isFocused || state.menuIsOpen);
    const variantStyles = {
      height: '40px',
      minHeight: '40px',
      borderRadius: '8px',
      backgroundColor: theme.colors.neutral50,
    };
    return {
      ...caller,
      ...variantStyles,
      ...addActiveBorder(caller, isActive),
      '&:hover': {
        ...((caller['&:hover' as keyof typeof caller] as StyleObj | undefined) ?? {}),
        borderColor: isActive ? theme.colors.blue500 : theme.colors.neutral400,
      },
    };
  };

  const menuStyle = (provided: StyleObj, state: SelectState): StyleObj => {
    /* eslint-disable @typescript-eslint/no-unsafe-argument */
    const caller = chakraStylesProp?.menu ? chakraStylesProp.menu(provided, state as any) : provided;
    /* eslint-enable @typescript-eslint/no-unsafe-argument */
    const isActive = Boolean(state.selectProps?.menuIsOpen);
    return addActiveBorder(caller, isActive);
  };

  const menuListStyle = (provided: StyleObj, state: SelectState): StyleObj => {
    /* eslint-disable @typescript-eslint/no-unsafe-argument */
    const caller = chakraStylesProp?.menuList ? chakraStylesProp.menuList(provided, state as any) : provided;
    /* eslint-enable @typescript-eslint/no-unsafe-argument */
    const isActive = Boolean(state.selectProps?.menuIsOpen);
    return {
      ...caller,
      ...(isActive ? { borderColor: 'transparent' } : {}),
    };
  };

  // Compose final chakraStyles configuration.
  const chakraStyles: ChakraStylesConfig<T, IsMulti, Group> = {
    ...(chakraStylesProp as object),
    container: containerStyle,
    control: controlStyle,
    menu: menuStyle,
    menuList: menuListStyle,
  };

  // ---------------------------------------------------------------------
  // Ensure the control is blurred when the menu closes so typing no longer
  // targets the (now-closed) select – improves UX inside the FilterBar.
  // ---------------------------------------------------------------------
  const handleMenuClose = () => {
    callerOnMenuClose?.();
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  };

  return (
    <ResistomapSelect<T, IsMulti, Group>
      {...(rest as any)}
      chakraStyles={chakraStyles}
      classNamePrefix={resistomapSelectClassNamePrefix}
      onMenuClose={handleMenuClose}
    />
  );
}

// Re-export the common classNamePrefix so callers do not need two imports.
export { resistomapSelectClassNamePrefix } from './resistomap-select';

interface Option {
  label: unknown;
  value: unknown;
}

// Local subset of state fields touched by style callbacks.
interface SelectState {
  isFocused?: boolean;
  menuIsOpen?: boolean;
  selectProps?: {
    menuIsOpen?: boolean;
  };
}
